import { useQuery } from '@tanstack/react-query';
import { useState, useRef, useCallback, useEffect } from 'react';
import type { SSEEvent } from '../api/types.js';
import { useApiClient } from './use-api-client.js';

export type ReplaySpeed = 0.5 | 1 | 2 | 4;
export type ReplayState = 'idle' | 'playing' | 'paused' | 'finished';

export interface UseSessionReplayResult {
  allEvents: SSEEvent[];
  visibleEvents: SSEEvent[];
  state: ReplayState;
  speed: ReplaySpeed;
  progress: number;
  currentIndex: number;
  totalEvents: number;
  isLoading: boolean;
  error: Error | null;

  play: () => void;
  pause: () => void;
  setSpeed: (speed: ReplaySpeed) => void;
  seekTo: (index: number) => void;
  reset: () => void;
}

export function useSessionReplay(serverId: string, sessionId: string): UseSessionReplayResult {
  const api = useApiClient();

  const { data: allEvents = [], isLoading, error } = useQuery({
    queryKey: ['session-replay', serverId, sessionId],
    queryFn: () => api.sessions.replayHistory(serverId, sessionId),
    staleTime: 5 * 60_000,
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [state, setState] = useState<ReplayState>('idle');
  const [speed, setSpeedValue] = useState<ReplaySpeed>(1);

  // Refs for values read inside timers/callbacks to avoid stale closures
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const speedRef = useRef(speed);
  const stateRef = useRef(state);
  const indexRef = useRef(currentIndex);

  speedRef.current = speed;
  stateRef.current = state;
  indexRef.current = currentIndex;

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleNext = useCallback((fromIndex: number, events: SSEEvent[]) => {
    if (fromIndex >= events.length - 1) {
      setState('finished');
      return;
    }

    const current = events[fromIndex];
    const next = events[fromIndex + 1];
    const currentTs = current.timestamp ? new Date(current.timestamp).getTime() : 0;
    const nextTs = next.timestamp ? new Date(next.timestamp).getTime() : 0;
    const rawDelay = nextTs > currentTs ? nextTs - currentTs : 0;
    // Cap delay at 3s before speed multiplier, minimum 20ms
    const delay = Math.max(20, Math.min(3000, rawDelay) / speedRef.current);

    timerRef.current = setTimeout(() => {
      const nextIndex = fromIndex + 1;
      setCurrentIndex(nextIndex);
      indexRef.current = nextIndex;
      if (nextIndex >= events.length - 1) {
        setState('finished');
        stateRef.current = 'finished';
      } else {
        scheduleNext(nextIndex, events);
      }
    }, delay);
  }, []);

  // Clean up on unmount
  useEffect(() => clearTimer, [clearTimer]);

  const play = useCallback(() => {
    if (allEvents.length === 0) return;

    const s = stateRef.current;
    if (s === 'finished' || s === 'idle') {
      setCurrentIndex(0);
      indexRef.current = 0;
      setState('playing');
      stateRef.current = 'playing';
      scheduleNext(0, allEvents);
    } else if (s === 'paused') {
      setState('playing');
      stateRef.current = 'playing';
      scheduleNext(indexRef.current, allEvents);
    }
  }, [allEvents, scheduleNext]);

  const pause = useCallback(() => {
    clearTimer();
    if (stateRef.current === 'playing') {
      setState('paused');
      stateRef.current = 'paused';
    }
  }, [clearTimer]);

  const setSpeed = useCallback((newSpeed: ReplaySpeed) => {
    setSpeedValue(newSpeed);
    speedRef.current = newSpeed;
    if (stateRef.current === 'playing') {
      clearTimer();
      scheduleNext(indexRef.current, allEvents);
    }
  }, [clearTimer, scheduleNext, allEvents]);

  const seekTo = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(index, allEvents.length - 1));
    clearTimer();
    setCurrentIndex(clamped);
    indexRef.current = clamped;
    if (clamped >= allEvents.length - 1) {
      setState('finished');
      stateRef.current = 'finished';
    } else if (stateRef.current === 'playing') {
      scheduleNext(clamped, allEvents);
    }
  }, [allEvents, clearTimer, scheduleNext]);

  const reset = useCallback(() => {
    clearTimer();
    setCurrentIndex(0);
    indexRef.current = 0;
    setState('idle');
    stateRef.current = 'idle';
  }, [clearTimer]);

  const visibleEvents = allEvents.slice(0, currentIndex + 1);
  const totalEvents = allEvents.length;
  const progress = totalEvents > 0 ? (currentIndex + 1) / totalEvents : 0;

  return {
    allEvents,
    visibleEvents,
    state,
    speed,
    progress,
    currentIndex,
    totalEvents,
    isLoading,
    error: error as Error | null,
    play,
    pause,
    setSpeed,
    seekTo,
    reset,
  };
}
